import {
    DIVIDER_SPACINGS,
    DIVIDER_THICKNESSES,
    LomdaV3BlockType,
} from "@/lib/utils/lomdaV3/types";
import { SPACER_HEIGHT } from "./blockRegistry";

const anyOf = (values: readonly string[]) =>
    values.map((v) => `'${v}'`).join(" | ");

/**
 * AI-facing metadata for each V3 block type. This is the "menu" the generation
 * prompts are built from: it tells the model WHAT a block renders, WHEN to pick
 * it, and HOW to fill its data shape correctly. English-only (model-facing) —
 * kept separate from the i18n BLOCK_TYPE_LABEL.
 *
 * `doNotEmit` marks blocks the AI must not generate in the current (V1) funnel —
 * those that need uploaded media / external URLs the AI can't produce (no
 * AI-generated images in V1), or blocks not yet ready for AI authoring.
 */
export interface BlockAIMeta {
    /** What the block renders / looks like to the learner. */
    description: string;
    /** Policy: when the AI SHOULD choose this block. */
    whenToUse: string;
    /** When NOT to choose it. */
    whenNotToUse?: string;
    /** Field-by-field rules for producing valid `data` for this block. */
    instructions: string;
    /** Container blocks that nest child blocks. */
    isContainer?: boolean;
    /** Excluded from generation in the current funnel. */
    doNotEmit?: boolean;
}

export const BLOCK_AI_META: Record<LomdaV3BlockType, BlockAIMeta> = {
    text: {
        description:
            "A rich-text block. Variants: 'text' (prose), 'heading-text' (a heading + paragraph), 'multi-columns' (2-4 parallel heading+text columns), 'quote' (a pull-quote), 'table' (an HTML data table), 'link' (an external resource card).",
        whenToUse:
            "The default for any explanatory prose, section intros, comparisons (multi-columns), notable statements (quote), or tabular data (table).",
        instructions:
            "Set data.variant, then the variant's fields. text: { variant:'text', html } — html is simple sanitized HTML (<p>, <strong>, <em>, <ul>/<li>, <h2>/<h3>); no <script>/<style>. heading-text: { variant:'heading-text', heading, text } — both plain/simple-HTML strings. multi-columns: { variant:'multi-columns', columns:[{heading,text}, ...] } 2-4 items. quote: { variant:'quote', quote, authorName, authorTitle, authorPhoto:'', showAuthorPhoto:false, align:'center' }. table: { variant:'table', html } where html is a complete <table><tbody>…</tbody></table>. link: { variant:'link', url, title, description, thumbnail:'' }. Do NOT emit variants 'two-columns' or 'three-columns' (deprecated) — use 'multi-columns'.",
    },
    "media-layout": {
        description:
            "One or more images/videos with optional caption heading+text.",
        whenToUse: "When you have actual image or video URLs to show.",
        whenNotToUse:
            "V1: never — the AI cannot produce image/video files. Use text or interactive blocks instead.",
        instructions:
            "Requires real media URLs in cells[].url / videoId; the AI cannot generate these in V1.",
        doNotEmit: true,
    },
    audio: {
        description: "A single audio clip.",
        whenToUse: "When narration/audio is available.",
        whenNotToUse: "V1: never — no audio source.",
        instructions: "{ url, name } require a real audio URL.",
        doNotEmit: true,
    },
    embed: {
        description: "An iframe embed of a third-party tool (Canva, Google, YouTube, H5P, …).",
        whenToUse: "When embedding a specific external interactive/media URL.",
        whenNotToUse: "V1: never — no external URLs to embed.",
        instructions: "{ provider, url } require a real provider URL.",
        doNotEmit: true,
    },
    html: {
        description: "A raw HTML snippet embed (advanced).",
        whenToUse: "Only for custom embeds no provider fits.",
        whenNotToUse: "V1: never — prefer the typed text block.",
        instructions: "{ html } raw HTML.",
        doNotEmit: true,
    },
    attachment: {
        description: "A downloadable document with name + size.",
        whenToUse: "When offering a file to download.",
        whenNotToUse: "V1: never — no file to attach.",
        instructions: "{ url, name, size } require a real file URL.",
        doNotEmit: true,
    },
    walkthrough: {
        description:
            "A guided multi-step flow mixing info screens and interactive image-hotspot steps.",
        whenToUse: "To simulate a UI/process click-through over screenshots.",
        whenNotToUse: "V1: never — image steps need uploaded screenshots.",
        instructions: "Requires step images the AI cannot produce in V1.",
        doNotEmit: true,
    },
    hotspot: {
        description: "An image with positioned clickable markers + labels.",
        whenToUse: "To annotate parts of a diagram/screenshot.",
        whenNotToUse: "V1: never — needs an uploaded image.",
        instructions: "{ image, … markers } require a real image URL.",
        doNotEmit: true,
    },
    accordion: {
        description:
            "Collapsible title/body sections under a title + description; one open at a time.",
        whenToUse:
            "FAQs, or grouping several related points the learner expands on demand to reduce clutter.",
        instructions:
            "{ title, description, items:[{ id, title, body }] } — 3-6 items. id is a short unique slug. body is plain or simple-HTML text. Each item.title is a short question/label; body is the answer/detail.",
    },
    tabs: {
        description:
            "Tabbed sections; each tab holds its own child blocks. Learner switches between tabs.",
        whenToUse:
            "Organizing parallel topics/categories the learner toggles between (e.g. 'Before / During / After').",
        instructions:
            "{ intro, items:[{ id, label, icon, blocks:[] }] } — 2-4 tabs. intro is a short heading+text (simple HTML). label is the tab name. icon is one of: users-round, palette, code, circle-check-big, lightbulb, target, book-open, layers, zap, flag, star, rocket, info, settings, circle. blocks is the tab's child blocks (generated separately) — emit it as []; children are filled in afterward.",
        isContainer: true,
    },
    checklist: {
        description: "A list of checkable text items under a title.",
        whenToUse:
            "Key takeaways, a recap, or an actionable to-do list the learner can tick off.",
        instructions:
            "{ title, items:[{ id, text }] } — 4-8 concise items. id is a short unique slug; text is one line.",
    },
    "flip-cards": {
        description:
            "A grid of cards (icon + title + subtitle) that flip to reveal back content.",
        whenToUse:
            "Term→definition, concept→detail, or teaser→reveal pairs that benefit from a flip interaction.",
        instructions:
            "{ title, description, cards:[{ id, icon, title, subtitle, back }] } — 3-6 cards. icon is one of the tab icon keys (users-round, palette, code, lightbulb, target, book-open, layers, zap, flag, star, rocket, info, settings, circle). title = front term; subtitle = short front label; back = the revealed explanation (simple HTML). Do NOT use the deprecated 'front' field.",
    },
    scenario: {
        description:
            "Branching dialogue scenes with quality-rated choices and per-choice feedback.",
        whenToUse:
            "Decision-making / soft-skills role-play where choices have consequences.",
        whenNotToUse: "V1: never — not yet ready for AI authoring.",
        instructions:
            "{ scenes:[{ id, name, dialogue, choices:[{ id, text, feedback, quality, nextSceneId }] }] } — 2-4 scenes, 2-4 choices each. quality is 'good' | 'average' | 'poor'. nextSceneId is '' (go to next scene in order), 'end' (finish), or the id of another scene to branch to — it MUST be '' , 'end', or an existing scene id in this block. dialogue sets the situation; feedback explains why a choice is good/poor.",
        doNotEmit: true,
    },
    "card-sorting": {
        description:
            "Drag cards into the correct category buckets; graded on submit.",
        whenToUse:
            "Categorization practice / checking the learner groups items correctly.",
        instructions:
            "{ title, description, categories:[{ id, title }], cards:[{ id, text, categoryId }] } — 2-3 categories, 4-8 cards. Every card.categoryId MUST equal one of the categories[].id (its correct bucket). ids are short unique slugs.",
    },
    "how-to": {
        description: "Ordered title/body steps, optionally with an image per step.",
        whenToUse: "A sequential procedure or tutorial.",
        instructions:
            "{ steps:[{ id, title, body, image }] } — 3-7 steps. id short slug; title is the step name; body is the instruction (simple HTML). Set image to '' (V1 has no images).",
        // Temporarily disabled (mirrors `inactive` in sectionRegistry). The AI
        // funnel reads this file, not the client registry, so it's set here too.
        doNotEmit: true,
    },
    "knowledge-check": {
        description:
            "Quiz questions (single- or multi-select) with immediate feedback.",
        whenToUse:
            "Assessing comprehension; great for ending a section/page with a check.",
        instructions:
            "{ title, description, questions:[{ id, text, multiple, options:[{ id, text, correct }] }] } — 1-4 questions, 3-4 options each. If multiple=false EXACTLY ONE option has correct=true; if multiple=true one OR MORE have correct=true. ids are short unique slugs.",
    },
    steps: {
        description:
            "A numbered timeline; clicking a step shows its content (child blocks) on the right.",
        whenToUse:
            "A sequential process, timeline, or staged lesson where each stage has richer content.",
        instructions:
            "{ intro, items:[{ id, title, subtitle, contentHeading, blocks:[] }] } — 3-6 steps. intro is a short heading+text. title is the short step label (e.g. a year or stage); subtitle is a one-line summary; contentHeading defaults to subtitle. blocks is the step's child blocks (generated separately) — emit []; children are filled in afterward.",
        isContainer: true,
    },
    columns: {
        description:
            "Two blocks side by side (stacked on mobile) — typically a short explanation next to an illustration.",
        whenToUse:
            "Pairing an explanation with a visual, so the page reads as a text/picture pair instead of another full-width paragraph. Also fits an explanation beside a short checklist.",
        instructions:
            "{ columns:[{ kind, brief }] } — EXACTLY 2 columns. kind is 'text', 'checklist' or 'image'; brief says what that column must hold (for 'image', the brief IS the English image prompt). Never two 'text' columns. Do not nest a columns block inside a columns block.",
        isContainer: true,
    },
    rating: {
        description: "A 1-5 rating (stars) the learner picks.",
        whenToUse: "Collecting a quick self-rating or reaction.",
        instructions: "{ question, max } — max defaults to 5.",
        doNotEmit: true,
    },
    poll: {
        description: "An opinion poll — pick one option, then see results.",
        whenToUse: "Gathering opinions with no correct answer.",
        instructions:
            "{ question, options:[{ id, text, votes }] } — votes seeds the demo results.",
        doNotEmit: true,
    },
    "word-cloud": {
        description:
            "The learner enters a few words, then sees a word cloud.",
        whenToUse: "Open-word reflection shown as a cloud.",
        instructions:
            "{ question, wordCount }.",
        doNotEmit: true,
    },
    "open-ended": {
        description:
            "A free-text question the learner answers in their own words; an AI bot then gives feedback on that answer.",
        whenToUse:
            "A reflection page: asking the learner to apply the idea to their own work or decide what they would do. There is no single right answer, so it makes the learner think rather than recall.",
        instructions:
            "{ question, guidance } — question is open (never a factual quiz question); guidance is one sentence telling the bot what a good answer contains.",
    },
    "complete-sentence": {
        description:
            "Fill-in-the-blank sentence where each blank is a dropdown of one correct word plus distractors.",
        whenToUse: "Testing recall of specific terms or figures.",
        instructions:
            "{ text, answers, explanation }. In text, mark each blank inline by wrapping the correct word in underscores, e.g. 'Water is made of _hydrogen_ and _oxygen_.' — every _word_ becomes a dropdown and its wrapped word is the correct answer. answers is a list PER BLANK, aligned by position: answers[0] holds the wrong words offered alongside the first _word_, answers[1] the second, and so on. Give each blank 2-3 plausible distractors. explanation is shown after a fully correct answer.",
    },
    chart: {
        description:
            "A single-series data chart in the lesson's brand colours. Variants: 'bar' (compare categories), 'line' (change over time), 'donut' (parts of a whole).",
        whenToUse:
            "ONLY when the SOURCE MATERIAL contains real figures you can point to — statistics, percentages, measured results, a breakdown, a series over time — and seeing them side by side teaches something the sentence cannot.",
        whenNotToUse:
            "NEVER invent, estimate or 'illustrate' numbers. If the source has no figures for this point, do not use a chart — use text, multi-columns or a table instead. Also skip it for two values you could simply state in a sentence, for qualitative ideas that have no measurement, and for anything you cannot attribute in the caption. At most ONE chart in a lomda: a chart added for visual variety is worse than no chart.",
        instructions:
            "{ variant, title, caption, unit, points }. Choose variant by what the numbers do: categories compared → 'bar'; a value changing across time/stages → 'line'; shares of one whole (they should total ~100) → 'donut'. title is a short plain-text heading (no HTML). points is 2-6 items of { label, value }: label is a SHORT category name (1-3 words, in the lesson language), value is a NUMBER copied from the source — never rounded into invention, never made up. unit is what the values are measured in ('%', '₪', 'hours') or '' when they are plain counts — a donut ignores it and always shows each slice's share of the total. caption is ONE sentence, in the lesson language, stating what the chart shows AND where the numbers come from (e.g. 'Share of the 2023 budget, by department — from the annual report, page 12'). If you cannot honestly write that source in the caption, you must NOT emit this block.",
    },
    spacer: {
        description: "Empty vertical space of a set height in pixels.",
        whenToUse: "Never — spacing is set by the theme and by the author.",
        instructions: `{ height } — pixels, ${SPACER_HEIGHT.min}-${SPACER_HEIGHT.max}.`,
        doNotEmit: true,
    },
    divider: {
        description: "A horizontal line between sections, in the theme's border colour.",
        whenToUse: "Never — dividers are placed by the author.",
        instructions: `{ appearance?: { thickness, spacing } } — thickness ${anyOf(DIVIDER_THICKNESSES)}; spacing ${anyOf(DIVIDER_SPACINGS)}. No color: it follows the theme.`,
        doNotEmit: true,
    },
    button: {
        description: "A single button that opens a website or another page of the lomda.",
        whenToUse: "Never — buttons are placed by the author.",
        instructions: "{ label, action: 'url' | 'page', url?, pageId? }",
        doNotEmit: true,
    },
};

/** Block types the AI is allowed to generate in the current funnel. */
export const AI_EMITTABLE_BLOCK_TYPES = (
    Object.keys(BLOCK_AI_META) as LomdaV3BlockType[]
).filter((t) => !BLOCK_AI_META[t].doNotEmit);

/** Compact menu string for the outline prompt (only emittable blocks). */
export function buildBlockMenuForPrompt(): string {
    return AI_EMITTABLE_BLOCK_TYPES.map((type) => {
        const m = BLOCK_AI_META[type];
        const tags = m.isContainer ? " [container]" : "";
        return `- "${type}"${tags}: ${m.description} USE WHEN: ${m.whenToUse}`;
    }).join("\n");
}

/** Per-type field-by-field instructions for the populate prompt. */
export function blockInstructions(type: LomdaV3BlockType): string {
    return BLOCK_AI_META[type].instructions;
}
